import React, { useState, useEffect, useRef } from 'react';
import { FaBug, FaLaptopCode, FaTools, FaCogs } from "react-icons/fa";
import './About.css';

function About() {
  const [whoAmIInView, setWhoAmIInView] = useState(false);
  const [skillsInView, setSkillsInView] = useState(false);
  const [experienceInView, setExperienceInView] = useState(false);

  const whoAmIRef = useRef(null);
  const skillsRef = useRef(null);
  const experienceRef = useRef(null);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.3,
    };

    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.target === whoAmIRef.current) setWhoAmIInView(entry.isIntersecting);
        if (entry.target === skillsRef.current) setSkillsInView(entry.isIntersecting);
        if (entry.target === experienceRef.current) setExperienceInView(entry.isIntersecting);
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    if (whoAmIRef.current) observer.observe(whoAmIRef.current);
    if (skillsRef.current) observer.observe(skillsRef.current);
    if (experienceRef.current) observer.observe(experienceRef.current);

    return () => {
      if (whoAmIRef.current) observer.unobserve(whoAmIRef.current);
      if (skillsRef.current) observer.unobserve(skillsRef.current);
      if (experienceRef.current) observer.unobserve(experienceRef.current);
    };
  }, []);

  return (
    <section id="about" className="About">
      <div className="about-header">
        <h2>ABOUT ME</h2>
      </div>

      <div className="about-content-wrapper">
        <div className="about-sections">

          {/* ------------------------- INTRODUCTION ------------------------- */}
          <div
            ref={whoAmIRef}
            className={`about-section-item ${whoAmIInView ? 'is-visible' : ''}`}
          >
            <h3>INTRODUCTION</h3>
            <p>
              I am a dedicated and results-driven Quality Assurance Engineer with 3 years of hands-on experience in both manual and automation testing, specializing in delivering high-quality, reliable software solutions across web and mobile platforms. My professional journey has been built on strong analytical thinking, attention to detail, and a commitment to ensuring seamless user experiences.
            </p>
            <p>
              Throughout my career, I have worked extensively with Selenium, Appium, TestNG, Java, JavaScript, and modern automation frameworks, enabling me to design scalable and maintainable test solutions. I have successfully automated critical workflows, optimized test coverage, reduced execution time, and actively contributed to improving the overall software development lifecycle. My work includes leading mobile automation initiatives, maintaining automation frameworks, and mentoring junior QA engineers—fostering a culture of quality, collaboration, and continuous improvement within engineering teams.
            </p>
            <p>
            I have experience working in Agile/Scrum environments, closely collaborating with developers, product owners, and stakeholders to clarify requirements, identify risks, and ensure that releases meet the highest quality standards. Whether it is building new test frameworks, executing regression suites, validating APIs, or performing end-to-end testing, I approach each task with a methodical and process-driven mindset.
            </p>
            <p>
            Academically, I hold a B.Sc. (Honours) in Computation and Management from the University of Peradeniya, where I also held leadership roles including President of the Computation and Management Society. Building on my technical foundation, I am now pursuing a Master’s degree in Cybersecurity at the University of Pisa (EQF Level 7), further expanding my knowledge in secure software development, vulnerability assessment, and modern security practices—an increasingly vital component in today’s technology landscape.
            </p>
            <p>
            I am passionate about continuous learning, innovation, and building robust automation that improves product quality and streamlines development processes. My goal is to combine my QA expertise with my growing cybersecurity knowledge to contribute to secure, high-performing engineering teams and create technology that is both reliable and resilient
            </p>
          </div>

          {/* --------------------------- SKILLS --------------------------- */}
          <div
            ref={skillsRef}
            className={`about-section-item ${skillsInView ? 'is-visible' : ''}`}
          >
            <h3>SKILLS</h3>

            <section id="skills" className="Skills">
              <div className="skills-grid">

                {/* Manual Testing */}
                <div className="skill-card">
                  <FaBug className="skill-icon" />
                  <h4>Manual Testing</h4>
                  <ul>
                    <li>Test Case Design, Execution & Reporting</li>
                    <li>Regression, Functional & Exploratory Testing</li>
                    <li>Requirement Analysis & Test Strategy Planning</li>
                    <li>Defect Identification, Tracking & Verification</li>
                    <li>Agile/Scrum Collaboration</li>
                  </ul>
                </div>

                {/* Automation Testing */}
                <div className="skill-card">
                  <FaLaptopCode className="skill-icon" />
                  <h4>Automation Testing</h4>
                  <ul>
                    <li>Selenium WebDriver (Java)</li>
                    <li>Appium (Mobile Automation)</li>
                    <li>Cypress, Playwright, Puppeteer</li>
                    <li>API Testing – Postman, Rest Assured</li>
                    <li>Performance Testing – JMeter</li>
                    <li>TestNG, JUnit</li>
                    <li>BDD Framework – Cucumber</li>
                    <li>Framework Design (POM, Data-Driven, Hybrid)</li>
                  </ul>
                </div>

                {/* Tools */}
                <div className="skill-card">
                  <FaTools className="skill-icon" />
                  <h4>Programming & Tools</h4>
                  <ul>
                    <li>Java, JavaScript, Python, PHP</li>
                    <li>HTML5, CSS3, SCSS, LESS</li>
                    <li>SQL, JSON, REST APIs</li>
                    <li>Git, GitHub, GitLab, Bitbucket</li>
                    <li>Jira, Confluence, ClickUp</li>
                    <li>Jenkins, Bamboo, Hudson</li>
                    <li>Maven, Gradle</li>
                    <li>SonarQube</li>
                  </ul>
                </div>

                {/* Methods */}
                <div className="skill-card">
                  <FaCogs className="skill-icon" />
                  <h4>Methods & Technologies</h4>
                  <ul>
                    <li>Agile & Scrum</li>
                    <li>SDLC & STLC</li>
                    <li>Page Object Model</li>
                    <li>Mobile Testing (Android/iOS)</li>
                    <li>Web Application Testing</li>
                    <li>CI/CD</li>
                    <li>Automation Framework Maintenance</li>
                  </ul>
                </div>

              </div>
            </section>
          </div>

          {/* ------------------------ EXPERIENCE ------------------------ */}
          <div
            ref={experienceRef}
            className={`about-section-item ${experienceInView ? 'is-visible' : ''}`}
          >
            <h3>WORK EXPERIENCE</h3>

            {/* QA Engineer */}
            <div className="experience-item">
              <h4>Quality Assurance Engineer - 4 Axis Solutions (Pvt) Ltd</h4>
              <p className="experience-location">
                Kollupitiya, Sri Lanka | <a href="https://www.4axis.com">4axis.com</a>
              </p>
              <ul>
                <li>Led mobile test automation with Appium + Java.</li>
                <li>Mentored junior QA engineers and improved QA processes.</li>
                <li>Defined test strategies and optimized workflows.</li>
              </ul>
            </div>

            {/* Associate QA Engineer */}
            <div className="experience-item">
              <h4>Associate QA Engineer - 4 Axis Solutions (Pvt) Ltd</h4>
              <p className="experience-location">
                Kollupitiya, Sri Lanka | <a href="https://www.4axis.com">4axis.com</a>
              </p>
              <ul>
                <li>Managed end-to-end testing across multiple projects.</li>
                <li>Worked closely with developers & product teams.</li>
                <li>Tracked and reported defects using JIRA.</li>
              </ul>
            </div>

            {/* Trainee QA Engineer */}
            <div className="experience-item">
              <h4>Trainee QA Engineer - Parallax Technologies (Pvt) Ltd</h4>
              <p className="experience-duration">1 Jan 2024 – 31 Jul 2024</p>
              <p className="experience-location">
                Nugegoda, Sri Lanka | <a href="https://parallaxtec.com">parallaxtec.com</a>
              </p>
              <ul>
                <li>Reduced regression execution time by 60% using Selenium.</li>
                <li>Trained new QA hires.</li>
                <li>Supported planning, execution & reporting.</li>
              </ul>
            </div>

            {/* Intern QA Engineer */}
            <div className="experience-item">
              <h4>Intern QA Engineer - Parallax Technologies (Pvt) Ltd</h4>
              <p className="experience-duration">1 May 2023 – 31 Dec 2023</p>
              <p className="experience-location">
                Nugegoda, Sri Lanka | <a href="https://parallaxtec.com">parallaxtec.com</a>
              </p>
              <ul>
                <li>Automated 300+ test cases.</li>
                <li>Integrated QA practices into the SDLC.</li>
                <li>Strengthened team quality culture.</li>
              </ul>
            </div>

          </div> {/* End Experience */}

        </div>
      </div>

    </section>
  );
}

export default About;
